/**
 * Real-time backup/restore events (mirror of the backend `/backup` gateway).
 *
 * The backup page already polls progress; this socket delivers the same
 * payloads the moment they happen, so a multi-minute restore no longer keeps a
 * 1s HTTP poll running. Polling stays in place as a fallback: when the socket
 * is not connected the hooks keep their interval, and a missed event cannot
 * leave a progress card stuck.
 */
import { useSyncExternalStore } from 'react';
import { io, type Socket } from 'socket.io-client';
import { ENV } from '../config/env';
import type { BackupProgress, BackupRecord, RestoreResult } from '../types/backup';

export type BackupSocketEvent =
  | {
      type: 'backup:progress';
      payload: {
        recordId?: string;
        jobId: string;
        progress: BackupProgress;
      };
    }
  | {
      type: 'backup:finished';
      payload: {
        recordId: string;
        jobId: string;
        state: string;
        record?: BackupRecord | null;
        progress?: BackupProgress | null;
        errorMessage?: string | null;
      };
    }
  | {
      type: 'restore:progress';
      payload: { jobId: string; progress: BackupProgress };
    }
  | {
      type: 'restore:finished';
      payload: {
        jobId: string;
        state: string;
        result?: RestoreResult | null;
        error?: string | null;
      };
    }
  | { type: 'queue:changed'; payload: Record<string, any> };

let socket: Socket | null = null;
let connected = false;
const listeners = new Set<(event: BackupSocketEvent) => void>();
const statusListeners = new Set<() => void>();

function emit(event: BackupSocketEvent): void {
  for (const listener of listeners) {
    try {
      listener(event);
    } catch {
      /* one bad subscriber must not break the others */
    }
  }
}

function setConnected(next: boolean): void {
  if (connected === next) return;
  connected = next;
  for (const listener of statusListeners) listener();
}

/** Opens the namespace once. Safe to call from every mount. */
export function startBackupSocket(): void {
  if (socket) return;

  socket = io(`${ENV.VITE_API_URL}/backup`, {
    withCredentials: true,
    transports: ['websocket', 'polling'],
    path: '/socket.io',
  });

  socket.on('connect', () => setConnected(true));
  socket.on('disconnect', () => setConnected(false));
  socket.on('connect_error', () => setConnected(false));

  const events = [
    'backup:progress',
    'backup:finished',
    'restore:progress',
    'restore:finished',
    'queue:changed',
  ] as const;
  for (const name of events) {
    socket.on(name, (payload: any) =>
      emit({ type: name, payload } as BackupSocketEvent),
    );
  }
}

/** Closes the socket (page unmount / logout). */
export function stopBackupSocket(): void {
  socket?.disconnect();
  socket = null;
  setConnected(false);
}

export function subscribeBackupSocket(
  listener: (event: BackupSocketEvent) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function isBackupSocketConnected(): boolean {
  return connected;
}

function subscribeStatus(listener: () => void): () => void {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
}

/** Re-renders when the socket connects/disconnects (drives the polling fallback). */
export function useBackupSocketConnected(): boolean {
  return useSyncExternalStore(
    subscribeStatus,
    isBackupSocketConnected,
    () => false,
  );
}
